/**
 * A seed packet Grow could not place, and Yes to "Grow thinks this is …".
 *
 * usePacketSuggestion: the packet's own suggestion, as the reader sent it;
 * for a scan read before suggestions were sent (one kept on an Add photos
 * photo, say) a free look-up by the packet's printed name (POST
 * /plants/identify with free, never a model: nothing is counted), so the
 * owner's Monarda packet is said plainly too.
 *
 * useAcceptSuggestion: the packet as it is filed on Yes. Under a broad flower
 * parent it is the reader's own filing (`as`: "Bee Balm" under Perennial
 * Flower), or the packet's printed variety with the flower's name for the
 * server to join to it (joinKind: "Jacob Cline" and "Bee Balm" are filed as
 * "Jacob Cline Bee Balm"; the server names it, audit A35); one Grow has by
 * another name is that plant; under any other plant the gardener's own plant
 * is made first (POST /plants/own) and the packet's printed variety goes
 * under it. Every placed packet says joinKind, null for a name already whole,
 * so the printed kind is not joined to it.
 *
 * Who uses it: PacketReview and the Add photos packet card (PacketPile).
 * Caching: the look-up under ['packet-suggestion', name] for
 * CATALOGUE_STALE_TIME_MS; filing an own plant marks the plant picker's answers
 * stale (pickerStale), so every picker lists it.
 *
 * @module acceptSuggestion
 */

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fileOwnPlant, identifyPlant, type PlantSuggestion } from '@/shared/api/plantNames';
import type { PacketFiling, ScannedPacket } from '@/shared/api/packets';
import { titleName } from '@/shared/components/plant-fields/useFileSuggestion';
import { CATALOGUE_STALE_TIME_MS, pickerStale } from '@/shared/components/plant-fields/usePlantPicker';

/** A suggestion, with the reader's own filing under a broad flower parent when it sent one. */
export type SuggestionWithFiling = PlantSuggestion & { as?: PacketFiling | null };

/**
 * What Grow thinks an unplaced packet is: its own suggestion, else a free
 * look-up by its printed name; null for a placed packet or one Grow knows
 * nothing of.
 *
 * @param packet - The packet as read.
 * @returns The suggestion, or null (also while the look-up runs).
 */
export function usePacketSuggestion(packet: ScannedPacket): SuggestionWithFiling | null {
  const lookUp = packet.plantId === null && !packet.suggestion && Boolean(packet.kind?.trim());
  const { data } = useQuery({
    queryKey: ['packet-suggestion', packet.kind?.trim().toLowerCase()],
    queryFn: async () => (await identifyPlant(packet.kind.trim(), true)).suggestion,
    enabled: lookUp,
    staleTime: CATALOGUE_STALE_TIME_MS,
    retry: false,
  });
  if (packet.plantId !== null) return null;
  return packet.suggestion ?? (lookUp ? data ?? null : null);
}

/**
 * Yes to a packet's suggestion: the packet as it is then filed.
 *
 * @returns accept(packet, suggestion): the packet placed.
 */
export function useAcceptSuggestion(): (packet: ScannedPacket, suggestion: SuggestionWithFiling) => Promise<ScannedPacket> {
  const queryClient = useQueryClient();
  return React.useCallback(async (packet: ScannedPacket, suggestion: SuggestionWithFiling): Promise<ScannedPacket> => {
    const base = { ...packet, suggestion: null };
    if (suggestion.known) {
      return {
        ...base, plantId: suggestion.plantId, plantName: suggestion.plantName, varietyId: null, varietyName: null,
        newVariety: packet.variety, joinKind: null,
      };
    }
    if (suggestion.broad) {
      const as = suggestion.as ?? null;
      if (as?.plantId) return { ...base, ...as, joinKind: null };
      // Under the broad parent the flower's name is its variety, the printed one before it, which the server joins:
      // "Jacob Cline Bee Balm"; with none printed, "Bee Balm".
      const name = titleName(suggestion.name);
      return {
        ...base, plantId: suggestion.plantId, plantName: suggestion.plantName, varietyId: null, varietyName: null,
        newVariety: packet.variety ?? name, joinKind: name,
      };
    }
    // Their own plant, in the group of the plant it grows most like; its printed variety under it.
    const filed = await fileOwnPlant(titleName(suggestion.name), suggestion.plantId);
    await pickerStale(queryClient);
    return {
      ...base, plantId: filed.plantId, plantName: filed.name,
      varietyId: packet.variety ? null : filed.varietyId, varietyName: null, newVariety: packet.variety, joinKind: null,
    };
  }, [queryClient]);
}
